import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { FiEdit2, FiExternalLink, FiPaperclip, FiPlus, FiTrash2 } from 'react-icons/fi';
import { db, withTimeout } from '../../lib/firebase';
import { allows } from '../../lib/permissions';
import { formatDate, toInputDate } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { documentError } from '../../lib/documents';
import { BUTTON, Field, Modal, Notice, Panel, SIZE, SkeletonRows, TextInput } from '../ui/kit';
import AttachmentsField, { DocumentIcon, useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentViewer from '../documents/DocumentViewer';
import { archiveDocuments, fileAccess, removeFile, saveFile, storedDocuments, today, uploadFiles } from './fileModel';

const ICON_BUTTON = 'flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] print:hidden';
const CHIP = 'inline-flex max-w-full items-center gap-1 break-all text-left text-[13px] font-medium text-brand-600 no-underline transition hover:text-brand-700';

const emptyForm = () => ({ ordre: '', datePatient: today(), links: [], documents: [], removed: [], pending: [] });

// Documents filed in this patient file: listed, opened, added, edited and removed here.
const PatientFiles = ({ patient, user, rights }) => {
  const [state, setState] = useState({ status: 'loading', items: [] });
  const [dialog, setDialog] = useState(null); // { mode: 'form' | 'delete', file? }
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null); // { index, ratio } while files are sent
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');
  const [viewing, setViewing] = useState(null); // { item, file }
  const status = useDocumentsStatus();
  const access = fileAccess(user, rights, patient);
  const canDownload = allows(rights, 'fichiers', 'tout');

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'fichiers'), where('patientId', '==', patient._id)),
        (snapshot) => {
          const items = snapshot.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .sort((a, b) => new Date(b.datePatient || b.createdAt || 0) - new Date(a.datePatient || a.createdAt || 0));
          setState({ status: 'ready', items });
        },
        () => setState({ status: 'error', items: [] })
      ),
    [patient._id]
  );

  const openForm = (file) => {
    setForm(
      file
        ? { ...emptyForm(), ordre: file.ordre || '', datePatient: toInputDate(file.datePatient) || today(), links: normalizeLinks(file.dropboxLinks), documents: storedDocuments(file) }
        : emptyForm()
    );
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'form', file });
  };
  const openDelete = (file) => {
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'delete', file });
  };
  // Escape in the document viewer must not close the form below it.
  const close = () => !busy && !viewing && setDialog(null);

  const save = async () => {
    const editing = dialog.file;
    if (!form.links.length && !form.documents.length && !form.pending.length) {
      setDialogError('Ajoutez au moins un document.');
      return;
    }
    setBusy(true);
    setDialogError('');
    let documents = form.documents;
    try {
      // Files sent first: once in the service's Dropbox, they stay listed in the form if a later step fails.
      if (form.pending.length) {
        try {
          documents = [...documents, ...(await uploadFiles(form.pending, patient, setSending))];
          setForm((current) => ({ ...current, documents, pending: [] }));
        } catch (error) {
          const sent = error.sent || [];
          setForm((current) => ({ ...current, documents: [...current.documents, ...sent], pending: current.pending.slice(sent.length) }));
          throw error;
        }
      }
      if (form.removed.length) {
        const failed = await archiveDocuments(form.removed);
        if (failed.length) {
          setForm((current) => ({ ...current, documents: [...documents, ...failed], removed: [] }));
          throw new Error('archive');
        }
      }
      // Offline, the write waits in the queue and already shows in the list.
      await withTimeout(saveFile(user, patient, { ...form, documents }, editing), 15000);
      setNotice(editing ? 'Documents mis à jour.' : 'Documents ajoutés au dossier.');
      setDialog(null);
    } catch (error) {
      setDialogError(
        error.message === 'archive'
          ? 'Un document n’a pas pu être retiré. Réessayez.'
          : await documentError(error, 'L’enregistrement a échoué. Vérifiez la connexion, puis réessayez.')
      );
    } finally {
      setSending(null);
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      const stored = storedDocuments(dialog.file);
      const failed = await archiveDocuments(stored);
      if (failed.length) {
        // Keep the entry with the documents still in place.
        await withTimeout(saveFile(user, patient, { ordre: dialog.file.ordre || '', datePatient: dialog.file.datePatient || '', links: normalizeLinks(dialog.file.dropboxLinks), documents: failed }, dialog.file), 15000);
        throw new Error('archive');
      }
      await withTimeout(removeFile(dialog.file), 15000);
      setNotice(stored.length ? 'Documents retirés du dossier. Ils restent conservés dans les archives du Dropbox du service.' : 'Documents retirés du dossier. Les fichiers restent dans Dropbox.');
      setDialog(null);
    } catch (error) {
      setDialogError(error.message === 'archive' ? 'Certains documents n’ont pas pu être retirés. Réessayez.' : 'La suppression a échoué.');
    } finally {
      setBusy(false);
    }
  };

  // Taking a stored document out of the file sends it to the archives: deletion level.
  const canRemoveStored = dialog && dialog.file ? access.canDelete(dialog.file) : access.canAdd && allows(rights, 'fichiers', 'supprimer');
  const removeStored = (item) =>
    setForm((current) => ({ ...current, documents: current.documents.filter((entry) => entry.id !== item.id), removed: [...current.removed, item] }));

  return (
    <Panel
      title="Fichiers"
      action={
        access.canAdd && (
          <button type="button" onClick={() => openForm(null)} className={`${BUTTON.ghost} h-8 px-[12px] text-[13px] print:hidden`}>
            <FiPlus size={14} aria-hidden="true" />
            Ajouter
          </button>
        )
      }
    >
      {notice && (
        <div className="mb-[12px] print:hidden">
          <Notice tone="green">{notice}</Notice>
        </div>
      )}
      {state.status === 'loading' ? (
        <SkeletonRows rows={1} />
      ) : state.status === 'error' ? (
        <Notice tone="amber">Fichiers indisponibles pour le moment.</Notice>
      ) : state.items.length === 0 ? (
        <p className="m-0 text-[13.5px] text-black/45">Aucun fichier dans ce dossier.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-[12px] p-0">
          {state.items.map((file) => {
            const stored = storedDocuments(file);
            const links = normalizeLinks(file.dropboxLinks);
            return (
              <li key={file.id} className="flex gap-[12px] rounded-2xl bg-mist px-[14px] py-[12px]">
                <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-white text-sky-600">
                  <FiPaperclip size={15} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-[14px] font-semibold text-ink">{formatDate(file.datePatient) || 'Date non précisée'}</p>
                  {file.ordre && <p className="mb-0 mt-0.5 break-words text-[13px] text-black/55">{file.ordre}</p>}
                  {(stored.length > 0 || links.length > 0) && (
                    <ul className="mb-0 mt-2 flex list-none flex-col gap-1 p-0">
                      {stored.map((item) => (
                        <li key={item.id}>
                          <button type="button" onClick={() => setViewing({ item, file })} className={`${CHIP} cursor-pointer border-0 bg-[transparent] p-0`}>
                            <DocumentIcon name={item.name} mimeType={item.mimeType} size={12} />
                            {item.name}
                          </button>
                        </li>
                      ))}
                      {links.map((item, index) => (
                        <li key={`${item.link}-${index}`}>
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className={CHIP}>
                            <FiExternalLink size={12} aria-hidden="true" className="flex-none" />
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {(access.canEdit(file) || access.canDelete(file)) && (
                  <div className="-mr-1 -mt-1 flex flex-none items-start">
                    {access.canEdit(file) && (
                      <button type="button" onClick={() => openForm(file)} aria-label="Modifier" title="Modifier" className={`${ICON_BUTTON} text-black/45 hover:bg-white hover:text-brand-600`}>
                        <FiEdit2 size={14} aria-hidden="true" />
                      </button>
                    )}
                    {access.canDelete(file) && (
                      <button type="button" onClick={() => openDelete(file)} aria-label="Supprimer" title="Supprimer" className={`${ICON_BUTTON} text-red-500 hover:bg-red-50`}>
                        <FiTrash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={Boolean(dialog && dialog.mode === 'form')}
        title={dialog && dialog.file ? 'Modifier les documents' : 'Ajouter des documents'}
        onClose={close}
        footer={
          <>
            <button type="button" onClick={close} disabled={busy} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={save} disabled={busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {sending ? `Envoi ${sending.index + 1}/${form.pending.length || sending.index + 1}…` : busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-[18px]">
          <p className="m-0 text-[14px] text-black/55">
            Dossier de <strong className="text-ink">{patient.nom}</strong>
            {patient.dossierNumber ? ` · ${patient.dossierNumber}` : ''}
          </p>
          <Field id="file-date" label="Date">
            <TextInput id="file-date" type="date" value={form.datePatient} onChange={(e) => setForm({ ...form, datePatient: e.target.value })} />
          </Field>
          <Field id="file-summary" label="Résumé" hint="Ce que contiennent ces documents (ex. bilan pré-opératoire).">
            <TextInput id="file-summary" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} />
          </Field>
          <Field id="file-documents" label="Documents" required>
            <AttachmentsField
              id="file-documents"
              status={status}
              stored={form.documents}
              onOpen={(item) => setViewing({ item, file: dialog && dialog.file })}
              onRemoveStored={canRemoveStored && !busy ? removeStored : null}
              links={form.links}
              onLinksChange={busy ? null : (links) => setForm((current) => ({ ...current, links }))}
              pending={form.pending}
              onPendingChange={(pending) => setForm((current) => ({ ...current, pending }))}
              sending={sending}
            />
          </Field>
          {form.removed.length > 0 && (
            <p className="m-0 text-[12.5px] text-black/45">
              {form.removed.length === 1 ? 'Un document sera retiré' : `${form.removed.length} documents seront retirés`} à l’enregistrement (conservés dans les archives du
              Dropbox du service).
            </p>
          )}
          {dialogError && <Notice tone="red">{dialogError}</Notice>}
        </div>
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'delete')}
        title="Retirer ces documents"
        onClose={close}
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Retirer du dossier'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          {dialog && dialog.file && dialog.file.ordre ? `« ${dialog.file.ordre} » sera retiré` : 'Ces documents seront retirés'} du dossier de{' '}
          <strong className="text-ink">{patient.nom}</strong>.{' '}
          {dialog && dialog.file && storedDocuments(dialog.file).length
            ? 'Ils restent conservés dans les archives du Dropbox du service.'
            : 'Les fichiers restent dans Dropbox.'}
        </p>
        {dialogError && (
          <div className="mt-[12px]">
            <Notice tone="red">{dialogError}</Notice>
          </div>
        )}
      </Modal>

      {viewing && (
        <DocumentViewer
          item={viewing.item}
          onClose={() => setViewing(null)}
          canReplace={Boolean(viewing.file) && access.canEdit(viewing.file)}
          canDownload={canDownload}
        />
      )}
    </Panel>
  );
};

export default PatientFiles;
